import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { pedidoApi, clienteApi, referenciaApi, campoApi } from '../services/api';
import { PageHeader } from '../components/ui';
import { formatarMoeda } from '../lib/format';
import {
  Plus, Trash2, Save, ArrowLeft, User, ShoppingBag,
  FileText, CheckCircle2, Scissors, CreditCard,
} from 'lucide-react';

const fluxos = [
  { value: 'PRODUCAO_COMPLETA', label: 'Produção Completa (Malha → Corte → Costura)' },
  { value: 'PECA_PRONTA', label: 'Peça Pronta (Fornecedor → Personalização)' },
  { value: 'PECA_CLIENTE', label: 'Peça do Cliente (Cliente entrega → Personaliza)' },
];

const formasPagamento = [
  { value: '', label: 'Selecionar...' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'PIX', label: 'PIX' },
  { value: 'CARTAO_DEBITO', label: 'Cartão Débito' },
  { value: 'CARTAO_CREDITO', label: 'Cartão Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
];

const CAMPOS_ESTRUTURAIS = ['produto', 'tecido', 'cor', 'tamanho'];

interface Campo {
  id: string;
  nome: string;
  label: string;
  tipoCampo: string; // TEXTO | SELECAO | CHECKBOX | NUMERO | DATA
  tipoReferencia?: string | null;
  obrigatorio: boolean;
  ativo: boolean;
  ordem: number;
  padrao: boolean;
}

interface ItemForm {
  id: string;
  produto: string;
  tecido: string;
  cor: string;
  tamanho: string;
  quantidade: number;
  valorUnitario: number;
  extras: Record<string, any>;
}

const itemVazio = (): ItemForm => ({
  id: crypto.randomUUID(),
  produto: '',
  tecido: '',
  cor: '',
  tamanho: '',
  quantidade: 1,
  valorUnitario: 0,
  extras: {},
});

/* ══ Campo dinâmico (texto, seleção, checkbox, número, data) ══ */
function CampoDinamico({
  campo,
  valor,
  onChange,
  opcoes,
  compacto = true,
}: {
  campo: Campo;
  valor: any;
  onChange: (v: any) => void;
  opcoes: string[];
  compacto?: boolean;
}) {
  const [outro, setOutro] = useState(false);
  const classe = compacto ? 'input-field !py-2 text-sm' : 'input-field';

  if (campo.tipoCampo === 'CHECKBOX') {
    return (
      <label className="flex items-center gap-2 h-[34px] text-sm text-ink-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!valor}
          onChange={e => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
        />
        {valor ? 'Sim' : 'Não'}
      </label>
    );
  }

  if (campo.tipoCampo === 'SELECAO' && !outro) {
    return (
      <select
        value={valor ?? ''}
        onChange={e => {
          if (e.target.value === '__OUTRO__') { setOutro(true); onChange(''); }
          else onChange(e.target.value);
        }}
        className={classe}
      >
        <option value="">Selecionar...</option>
        {opcoes.map(o => <option key={o} value={o}>{o}</option>)}
        <option value="__OUTRO__">✎ Outro (digitar)</option>
      </select>
    );
  }

  if (campo.tipoCampo === 'NUMERO') {
    return (
      <input
        type="number"
        step="any"
        value={valor ?? ''}
        onChange={e => onChange(e.target.value)}
        className={classe}
      />
    );
  }

  if (campo.tipoCampo === 'DATA') {
    return (
      <input
        type="date"
        value={valor ?? ''}
        onChange={e => onChange(e.target.value)}
        className={classe}
      />
    );
  }

  // TEXTO (ou SELECAO no modo "outro")
  return (
    <div className="relative">
      <input
        type="text"
        value={valor ?? ''}
        onChange={e => onChange(e.target.value)}
        className={classe}
        list={campo.tipoReferencia ? `dl-${campo.tipoReferencia}` : undefined}
      />
      {campo.tipoReferencia && (
        <datalist id={`dl-${campo.tipoReferencia}`}>
          {opcoes.map(o => <option key={o} value={o} />)}
        </datalist>
      )}
    </div>
  );
}

export function NovoPedidoPage() {
  const navigate = useNavigate();

  // Dados do cliente
  const [telefone, setTelefone] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState(false);

  // Dados do pedido
  const [prazo, setPrazo] = useState('');
  const [fluxo, setFluxo] = useState('PRODUCAO_COMPLETA');
  const [observacoes, setObservacoes] = useState('');
  const [informacoesProducao, setInformacoesProducao] = useState('');

  // Pagamento estruturado
  const [temEntrada, setTemEntrada] = useState(false);
  const [valorEntrada, setValorEntrada] = useState('');
  const [formaEntrada, setFormaEntrada] = useState('');
  const [entradaRecebida, setEntradaRecebida] = useState(true);
  const [numParcelas, setNumParcelas] = useState(1);
  const [comJuros, setComJuros] = useState(false);
  const [taxaJuros, setTaxaJuros] = useState('');
  const [diaVencimento, setDiaVencimento] = useState(10);
  const [formaParcelas, setFormaParcelas] = useState('');
  const [primeiroVencimento, setPrimeiroVencimento] = useState('');

  // Itens
  const [itens, setItens] = useState<ItemForm[]>([itemVazio()]);

  // Campos dinâmicos (configuração de Itens Padrão)
  const { data: campos } = useQuery({
    queryKey: ['campos-pedido'],
    queryFn: () => campoApi.listar().then(r => r.data as Campo[]),
  });
  const camposAtivos = (campos || []).filter(c => c.ativo).sort((a, b) => a.ordem - b.ordem);
  const camposEstruturais = camposAtivos.filter(c => CAMPOS_ESTRUTURAIS.includes(c.nome));
  const camposPersonalizados = camposAtivos.filter(c => !CAMPOS_ESTRUTURAIS.includes(c.nome));

  // Opções dos campos de seleção/texto (por tipo de referência)
  const tiposReferencia = [...new Set(camposAtivos.map(c => c.tipoReferencia).filter(Boolean))] as string[];
  const { data: todasReferencias } = useQuery({
    queryKey: ['referencias', 'todas', tiposReferencia.join(',')],
    queryFn: async () => {
      const resultado: Record<string, string[]> = {};
      for (const tipo of tiposReferencia) {
        const res = await referenciaApi.listar({ tipo, limit: 200 });
        resultado[tipo] = (res.data || []).map((r: any) => r.valor);
      }
      return resultado;
    },
    enabled: tiposReferencia.length > 0,
  });

  const buscarCliente = useCallback(async () => {
    if (telefone.length < 8) return;
    try {
      const res = await clienteApi.buscarPorTelefone(telefone.replace(/\D/g, ''));
      if (res.data) {
        setClienteId(res.data.id);
        setNomeCliente(res.data.nome);
        setClienteEncontrado(true);
      } else {
        setClienteEncontrado(false);
      }
    } catch {
      setClienteEncontrado(false);
    }
  }, [telefone]);

  useEffect(() => {
    // Prazo padrão: 15 dias
    const hoje = new Date();
    hoje.setDate(hoje.getDate() + 15);
    setPrazo(hoje.toISOString().split('T')[0]);
  }, []);

  const valorTotal = itens.reduce((sum, item) => sum + item.quantidade * item.valorUnitario, 0);
  const totalPecas = itens.reduce((sum, item) => sum + (item.quantidade || 0), 0);

  // Prévia das parcelas (mesma regra do backend: juros simples a.m.)
  const entrada = temEntrada ? parseFloat(valorEntrada) || 0 : 0;
  const valorFinanciado = Math.max(0, valorTotal - entrada);
  const taxa = comJuros ? (parseFloat(taxaJuros) || 0) / 100 : 0;
  const totalComJuros = valorFinanciado * (1 + taxa * numParcelas);
  const valorParcelaPreview = numParcelas > 0 ? Math.floor((totalComJuros / numParcelas) * 100) / 100 : 0;

  const criarPedido = useMutation({
    mutationFn: (data: any) => pedidoApi.criar(data),
    onSuccess: () => navigate('/pedidos'),
  });

  const updateItem = (id: string, campo: keyof ItemForm, valor: any) => {
    setItens(itens.map(i => (i.id === id ? { ...i, [campo]: valor } : i)));
  };

  const updateExtra = (id: string, nome: string, valor: any) => {
    setItens(itens.map(i => (i.id === id ? { ...i, extras: { ...i.extras, [nome]: valor } } : i)));
  };

  /** Converte o valor do campo conforme o tipo configurado. */
  const valorParaSalvar = (campo: Campo, valor: any) => {
    if (campo.tipoCampo === 'CHECKBOX') return valor ? 'Sim' : 'Não';
    if (valor === undefined || valor === null || valor === '') return '';
    return String(valor);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const itensPayload = itens.map(i => {
      const base: any = {
        quantidade: i.quantidade,
        valorUnitario: i.valorUnitario,
      };
      // Campos estruturais seguem a configuração de tipo, mas são
      // sempre enviados (backend exige); vazio vira "-".
      for (const nome of CAMPOS_ESTRUTURAIS) {
        const campo = camposEstruturais.find(c => c.nome === nome);
        const bruto = (i as any)[nome];
        base[nome] = campo ? (valorParaSalvar(campo, bruto) || '-') : bruto || '-';
      }
      const extras = Object.fromEntries(
        Object.entries(i.extras).filter(([, v]) => v !== '' && v !== undefined && v !== null),
      );
      if (Object.keys(extras).length) base.extras = extras;
      return base;
    });

    // Configuração estruturada de pagamento
    const pagamento: any = {};
    if (temEntrada && entrada > 0) {
      pagamento.entrada = {
        valor: entrada,
        formaPagamento: formaEntrada || undefined,
        recebida: entradaRecebida,
      };
    }
    if (valorFinanciado > 0) {
      pagamento.parcelas = {
        quantidade: numParcelas,
        comJuros,
        taxaJuros: comJuros ? parseFloat(taxaJuros) || 0 : undefined,
        diaVencimento,
        formaPagamento: formaParcelas || undefined,
        primeiroVencimento: primeiroVencimento
          ? new Date(`${primeiroVencimento}T12:00:00`).toISOString()
          : undefined,
      };
    }

    criarPedido.mutate({
      clienteId: clienteId || undefined,
      telefone: telefone.replace(/\D/g, ''),
      nomeCliente,
      prazo: new Date(prazo).toISOString(),
      formaPagamento: formaParcelas || formaEntrada || undefined,
      parcelas: numParcelas,
      valorEntrada: entrada || undefined,
      observacoes: observacoes || undefined,
      informacoesProducao: informacoesProducao || undefined,
      fluxoProducao: fluxo,
      itens: itensPayload,
      pagamento: Object.keys(pagamento).length ? pagamento : undefined,
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/pedidos"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-500 hover:text-ink-800 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar para pedidos
      </Link>

      <PageHeader
        titulo="Novo Pedido"
        descricao="Cadastre uma encomenda com itens, prazos e a configuração de pagamento."
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Cliente */}
        <section className="card">
          <h2 className="card-title flex items-center gap-2.5 mb-5">
            <span className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
              <User className="w-4 h-4 text-brand-600" />
            </span>
            Cliente
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Telefone *</label>
              <input
                type="tel"
                value={telefone}
                onChange={e => { setTelefone(e.target.value); setClienteEncontrado(false); }}
                onBlur={buscarCliente}
                placeholder="(44) 99999-9999"
                className="input-field"
                required
              />
              {clienteEncontrado && (
                <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 mt-2 animate-fade-in">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Cliente encontrado na base
                </p>
              )}
            </div>
            <div>
              <label className="input-label">Nome *</label>
              <input
                type="text"
                value={nomeCliente}
                onChange={e => setNomeCliente(e.target.value)}
                placeholder="Nome do cliente"
                className="input-field"
                required
              />
            </div>
          </div>
        </section>

        {/* Itens */}
        <section className="card">
          <div className="card-header">
            <h2 className="card-title flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-blue-600" />
              </span>
              Itens do Pedido
              <span className="badge bg-ink-100 text-ink-600 ring-1 ring-inset ring-ink-400/20">
                {itens.length}
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <Link to="/itens-padrao" className="text-[13px] font-semibold text-ink-400 hover:text-brand-600">
                Configurar campos
              </Link>
              <button
                type="button"
                onClick={() => setItens([...itens, itemVazio()])}
                className="btn-secondary !py-2 text-[13px]"
              >
                <Plus className="w-4 h-4" /> Adicionar item
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {itens.map((item, index) => (
              <div
                key={item.id}
                className="relative p-4 pt-5 bg-ink-50/60 rounded-xl ring-1 ring-inset ring-ink-200/60 animate-fade-in"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-md bg-ink-200/70 text-ink-600 text-2xs font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Item</span>
                </div>

                {/* Campos estruturais conforme configuração dinâmica */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {camposEstruturais.map(campo => (
                    <div key={campo.id}>
                      <label className="block text-xs font-semibold text-ink-500 mb-1">
                        {campo.label}{campo.obrigatorio ? ' *' : ''}
                      </label>
                      <CampoDinamico
                        campo={campo}
                        valor={(item as any)[campo.nome]}
                        onChange={v => updateItem(item.id, campo.nome as keyof ItemForm, v)}
                        opcoes={campo.tipoReferencia ? todasReferencias?.[campo.tipoReferencia] || [] : []}
                      />
                    </div>
                  ))}

                  <div>
                    <label className="block text-xs font-semibold text-ink-500 mb-1">Qtd *</label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantidade}
                      onChange={e => updateItem(item.id, 'quantidade', parseInt(e.target.value) || 1)}
                      className="input-field !py-2 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-500 mb-1">Valor Unit. *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.valorUnitario || ''}
                      onChange={e => updateItem(item.id, 'valorUnitario', parseFloat(e.target.value) || 0)}
                      placeholder="0,00"
                      className="input-field !py-2 text-sm"
                      required
                    />
                  </div>

                  {/* Campos personalizados */}
                  {camposPersonalizados.map(campo => (
                    <div key={campo.id}>
                      <label className="block text-xs font-semibold text-ink-500 mb-1">
                        {campo.label}{campo.obrigatorio ? ' *' : ''}
                      </label>
                      <CampoDinamico
                        campo={campo}
                        valor={item.extras[campo.nome]}
                        onChange={v => updateExtra(item.id, campo.nome, v)}
                        opcoes={campo.tipoReferencia ? todasReferencias?.[campo.tipoReferencia] || [] : []}
                      />
                    </div>
                  ))}

                  <div className="flex items-end">
                    <div className="w-full text-right">
                      <p className="text-2xs font-semibold text-ink-400 uppercase tracking-wider">Subtotal</p>
                      <p className="text-sm font-bold text-ink-900">{formatarMoeda(item.quantidade * item.valorUnitario)}</p>
                    </div>
                  </div>
                </div>

                {itens.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setItens(itens.filter(i => i.id !== item.id))}
                    className="absolute top-3 right-3 icon-btn !text-red-400 hover:!bg-red-50 hover:!text-red-600"
                    title="Remover item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="mt-5 flex items-center justify-between rounded-xl bg-ink-950 px-5 py-4">
            <div className="text-[13px] text-ink-400">
              {totalPecas} peça{totalPecas === 1 ? '' : 's'} em {itens.length} ite{itens.length === 1 ? 'm' : 'ns'}
            </div>
            <div className="text-right">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Total do pedido</p>
              <p className="font-display text-xl font-bold text-white">{formatarMoeda(valorTotal)}</p>
            </div>
          </div>
        </section>

        {/* Pagamento estruturado */}
        <section className="card">
          <h2 className="card-title flex items-center gap-2.5 mb-1.5">
            <span className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-emerald-600" />
            </span>
            Como o Cliente Vai Pagar
          </h2>
          <p className="text-[13px] text-ink-400 mb-5">
            Configure entrada e parcelas — elas viram lançamentos a receber vinculados ao pedido.
            Pedidos pagos pelo site da loja já chegam configurados.
          </p>

          {/* Entrada */}
          <div className="rounded-xl ring-1 ring-inset ring-ink-200/70 p-4 mb-4">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-ink-800 cursor-pointer">
              <input
                type="checkbox"
                checked={temEntrada}
                onChange={e => setTemEntrada(e.target.checked)}
                className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              Tem entrada (sinal)
            </label>
            {temEntrada && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-fade-in">
                <div>
                  <label className="input-label">Valor da Entrada</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-400">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={valorEntrada}
                      onChange={e => setValorEntrada(e.target.value)}
                      placeholder="0,00"
                      className="input-field !pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="input-label">Forma de Pagamento</label>
                  <select value={formaEntrada} onChange={e => setFormaEntrada(e.target.value)} className="input-field">
                    {formasPagamento.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={entradaRecebida}
                      onChange={e => setEntradaRecebida(e.target.checked)}
                      className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    />
                    Entrada já recebida
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Parcelas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="input-label">Parcelas do Restante</label>
              <input
                type="number"
                min="1"
                value={numParcelas}
                onChange={e => setNumParcelas(Math.max(1, parseInt(e.target.value) || 1))}
                className="input-field"
              />
            </div>
            <div>
              <label className="input-label">Forma de Pagamento</label>
              <select value={formaParcelas} onChange={e => setFormaParcelas(e.target.value)} className="input-field">
                {formasPagamento.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">Dia de Vencimento</label>
              <input
                type="number"
                min="1"
                max="28"
                value={diaVencimento}
                onChange={e => setDiaVencimento(Math.min(28, Math.max(1, parseInt(e.target.value) || 10)))}
                className="input-field"
              />
            </div>
            <div>
              <label className="input-label">1º Vencimento (opcional)</label>
              <input
                type="date"
                value={primeiroVencimento}
                onChange={e => setPrimeiroVencimento(e.target.value)}
                className="input-field"
              />
            </div>
          </div>

          {/* Juros */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-ink-800 cursor-pointer">
              <input
                type="checkbox"
                checked={comJuros}
                onChange={e => setComJuros(e.target.checked)}
                className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              Parcelas com juros
            </label>
            {comJuros && (
              <div className="flex items-center gap-2 animate-fade-in">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={taxaJuros}
                  onChange={e => setTaxaJuros(e.target.value)}
                  placeholder="0,0"
                  className="input-field !py-2 w-28"
                />
                <span className="text-sm text-ink-500">% ao mês (juros simples)</span>
              </div>
            )}
          </div>

          {/* Prévia */}
          {valorTotal > 0 && (
            <div className="mt-5 rounded-xl bg-ink-50/80 ring-1 ring-inset ring-ink-200/60 px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-[13px]">
              {temEntrada && entrada > 0 && (
                <span className="text-ink-600">
                  Entrada: <strong className="text-ink-900">{formatarMoeda(entrada)}</strong>
                  {!entradaRecebida && <span className="text-amber-600 font-semibold"> (a receber)</span>}
                </span>
              )}
              <span className="text-ink-600">
                Restante: <strong className="text-ink-900">{formatarMoeda(valorFinanciado)}</strong>
              </span>
              {valorFinanciado > 0 && (
                <span className="text-ink-600">
                  <strong className="text-ink-900">{numParcelas}× {formatarMoeda(valorParcelaPreview)}</strong>
                  {comJuros && taxa > 0 && (
                    <span className="text-ink-400"> (total c/ juros: {formatarMoeda(totalComJuros)})</span>
                  )}
                </span>
              )}
            </div>
          )}
        </section>

        {/* Produção */}
        <section className="card">
          <h2 className="card-title flex items-center gap-2.5 mb-5">
            <span className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <Scissors className="w-4 h-4 text-violet-600" />
            </span>
            Produção
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Prazo de Entrega *</label>
              <input
                type="date"
                value={prazo}
                onChange={e => setPrazo(e.target.value)}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="input-label">Fluxo de Produção</label>
              <select value={fluxo} onChange={e => setFluxo(e.target.value)} className="input-field">
                {fluxos.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="input-label">Observações</label>
              <textarea
                value={observacoes}
                onChange={e => setObservacoes(e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Informações adicionais sobre o pedido..."
              />
            </div>
            <div>
              <label className="input-label">Informações para Produção</label>
              <textarea
                value={informacoesProducao}
                onChange={e => setInformacoesProducao(e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Recados para corte, costura, personalização..."
              />
            </div>
          </div>
        </section>

        {/* Ações */}
        <div className="flex items-center justify-between gap-3 pb-4">
          <p className="hidden sm:flex items-center gap-2 text-[13px] text-ink-400">
            <FileText className="w-4 h-4" />
            O pedido entra como "Aguardando Entrada".
          </p>
          <div className="flex gap-3 ml-auto">
            <button type="button" onClick={() => navigate('/pedidos')} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={criarPedido.isPending} className="btn-primary">
              <Save className="w-4 h-4" />
              {criarPedido.isPending ? 'Salvando...' : 'Salvar Pedido'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
